import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as PdfIcon } from '../../assets/icons/pdf.svg';
import { NumericFormat } from 'react-number-format';
import Button from '../Button/Button';
import FormulaireAdministratif from '../FormulaireAdministratif/FormulaireAdministratif';
import ValidationPopUp from '../ValidationPopUp/ValidationPopUp';
import DropDownSignataires from '../DropDownSignataires/DropDownSignataires';

import './Candidature.css'
import { PrintButton } from '../PrintButton/PrintButton';
import { isPrestataireRole } from '../../utils/roleUtils';
import { useAuth } from '../../contexts/AuthContext';
import { getLotImage } from '../../utils/getLotImage';
import { getStatusColor } from '../../utils/entityUtils';

interface NumberFormatValues {
  formattedValue: string;
  value: string;
  floatValue?: number;
}

interface CandidatureProps {
  commande: any;
  setCommande: React.Dispatch<React.SetStateAction<any>>;
  handleExport: () => void;
  apiData?: any; // Nouvelle prop pour les données de l'API
  printRef?: any;
}

const Candidature: React.FC<CandidatureProps> = ({
  commande,
  setCommande,
  handleExport,
  apiData,
  printRef
}) => {
  const navigate = useNavigate();
  const [departementInfo, setDepartementInfo] = useState<any>(null);

  // Charger les informations du département depuis apiData si disponible
  useEffect(() => {
    if (apiData?.status === 'success' && apiData?.data) {
      setDepartementInfo(apiData.data);

      // Récupérer les données de l'opération depuis apiData
      const operationData = apiData.data.operation;

      // Mettre à jour la commande avec les informations complètes
      setCommande(prev => ({
        ...prev,
        operation: operationData, // Conserver toutes les données brutes de l'opération
        formulaire: {
          ...prev?.formulaire,
          // Données administratives
          nomOperation: operationData.nom || "Pas précisé",
          referenceInterne: operationData.reference || "Pas précisé",
          adresse: operationData.adresse || "Pas précisé",
          codePostal: operationData.code_postal || "Pas précisé",
          ville: operationData.ville || "Pas précisé",
          coordonneesMaitriseDOuvrage: operationData.coordonnees_maitrise_ouvrage || "Pas précisé",
          coordonneesMaitrise: operationData.coordonnees_maitrise || "Pas précisé",
          facturation: operationData.facturation || "Pas précisé",
          delaisConsultation: 10,
          delaisFinOperation: operationData.delais || operationData.delai_execution || 30,
          visitePreavis: operationData.visite === true,
          // ✅ CORRECTION: Utiliser condition_visite de l'API au lieu de conditionVisite
          conditionVisite: operationData.condition_visite || operationData.conditionVisite || "Pas précisé",
          besoin: operationData.besoin || "Pas précisé",
          dateDebutConsultation: new Date().toISOString().split('T')[0],
          dateDebutOperation: operationData.date_debut_operation || "Pas précisé",
          dateFinOperation: operationData.date_fin_operation || "Pas précisé",
          datePrevisionelle: operationData.expire_at || "Pas précisé",

          // Données géographiques
          lotGeographique: apiData.data.nom || "Pas précisé",
          regionName: apiData.data.region?.nom || "Pas précisé",

          // Ajouter l'objet operation complet pour accès facile
          operation: {
            ...operationData,
            visite: operationData.visite === true ? "Oui" : "Non",
            // ✅ CORRECTION: Utiliser condition_visite de l'API
            conditionVisite: operationData.visite === true ? (operationData.condition_visite || operationData.conditionVisite || "Pas précisé") : ""
          }
        }
      }));
    }
  }, [apiData, setCommande]);

  const [criteresErrors, setCriteresErrors] = useState<{ [key: string]: string }>({});
  const [prixErrors, setPrixErrors] = useState<{ [key: number]: boolean }>({});
  const [prixValidated, setPrixValidated] = useState(false);
  const [criteresSaving, setCriteresSaving] = useState(false);
  const [criteresSuccessMsg, setCriteresSuccessMsg] = useState('');
  const [criteresLoading, setCriteresLoading] = useState(false);
  const [reponseTechniqueError, setReponseTechniqueError] = useState('');
  const { user } = useAuth();
  // const contentRef = useRef<HTMLDivElement>(null);


  const isPrestataireView = isPrestataireRole(user?.role || '');

  // NOUVEAU: Fonction pour vérifier si la date est expirée
  const isDateExpired = (dateStr: string): boolean => {
    if (!dateStr) return false;

    const [datePart, timePart] = dateStr.split(' ');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour, minute, second] = timePart.split(':').map(Number);
    const targetDate = new Date(year, month - 1, day, hour, minute, second);
    const now = new Date();

    return now.getTime() >= targetDate.getTime();
  };

  // NOUVEAU: État pour gérer l'expiration (mis à jour dynamiquement)
  const [isOperationExpired, setIsOperationExpired] = useState(() => {
    const dateToCheck = commande?.expire_at || commande?.operation?.expire_at;
    const statusId = commande?.status?.id;
    const source = commande?.source || commande?.operation?.source;

    // Vérifier si expiré par statut (status 12 pour MS, status 9 pour BC)
    const isExpiredByStatus = (source === 'MS' && statusId === 12) || (source === 'BC' && statusId === 9);

    // Vérifier si expiré par date
    const isExpiredByDate = dateToCheck ? isDateExpired(dateToCheck) : false;

    return isExpiredByStatus || isExpiredByDate;
  });

  // NOUVEAU: Vérifier l'expiration au chargement et quand les dates ou statuts changent
  useEffect(() => {
    const dateToCheck = commande?.operation?.expire_at || commande?.expire_at;
    const statusId = commande?.status?.id;
    const source = commande?.source || commande?.operation?.source;

    console.log('🔍 Candidature - Vérification expiration:', {
      dateToCheck,
      statusId,
      source,
      isExpiredByDate: dateToCheck ? isDateExpired(dateToCheck) : false,
      isExpiredByStatus: (source === 'MS' && statusId === 12) || (source === 'BC' && statusId === 9),
      now: new Date().toISOString()
    });

    // Vérifier si expiré par statut
    const isExpiredByStatus = (source === 'MS' && statusId === 12) || (source === 'BC' && statusId === 9);

    // Vérifier si expiré par date
    const isExpiredByDate = dateToCheck ? isDateExpired(dateToCheck) : false;

    // Marquer comme expiré si l'un des deux critères est vrai
    setIsOperationExpired(isExpiredByStatus || isExpiredByDate);
  }, [commande?.expire_at, commande?.operation?.expire_at, commande?.status?.id, commande?.source, commande?.operation?.source]);

  // NOUVEAU: Écouter l'événement d'expiration du chrono
  useEffect(() => {
    const handleChronoExpired = () => {
      console.log('📢 Candidature: Chrono expiré - Rechargement imminent');
      setIsOperationExpired(true);
    };

    window.addEventListener('chronoExpired', handleChronoExpired);

    return () => {
      window.removeEventListener('chronoExpired', handleChronoExpired);
    };
  }, []);


  // État pour gérer le modal de confirmation de candidature
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  
  // État pour gérer le modal de refus de consultation
  const [isRefuserModalOpen, setIsRefuserModalOpen] = useState(false);
  
  // État pour gérer le modal de succès après refus
  const [isRefusSuccessModalOpen, setIsRefusSuccessModalOpen] = useState(false);

  // Initialisation des critères
  const criteresFormData = {
    criteres: {
      organisation: {
        reponse: commande.criteres?.organisation?.reponse || ''
      },
      moyens: {
        reponse: commande.criteres?.moyens?.reponse || ''
      },
      social: {
        reponse: commande.criteres?.social?.reponse || ''
      }
    }
  };

  // Récupération des données de la commande (API)
  useEffect(() => {
    const fetchCriteres = async () => {
      // Ajout d'un guard pour éviter les requêtes multiples
      if (criteresLoading) return;

      setCriteresLoading(true);
      try {
        const commandeId = window.location.pathname.split('/').pop() || '';
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_API_URL}/subsequents/commande/${commandeId}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        if (!response.ok) throw new Error('Erreur lors du chargement des critères');
        const data = await response.json();

        // Utiliser une fonction callback pour éviter les problèmes de stale closure
        setCommande(prev => {
          // Ne pas mettre à jour si les données sont identiques
          if (prev?.id === data.data.id &&
            prev?.criteres?.organisation?.reponse === data.data.note_technique?.description_humain) {
            return prev;
          }

          return {
            ...prev!,
            criteres: {
              organisation: {
                reponse: data.data.note_technique?.description_humain || ''
              },
              moyens: {
                reponse: data.data.note_technique?.description_operationnel || ''
              },
              social: {
                reponse: data.data.note_technique?.description_environnement || ''
              }
            },
            reponseTechnique: data.data.reponse || '',
            articlesPrestataire: (data.data.articles || []).map((art: any) => ({
              id: art.id,
              prixHT: art.pivot.prix ?? 0
            })),
            // Préserver l'état du signataire s'il existe déjà
            id_signataire: prev?.id_signataire || data.data.id_signataire,
            type_signataire: prev?.type_signataire || data.data.type_signataire
          };
        });
      } catch (e) {
        console.error(e);
      } finally {
        setCriteresLoading(false);
      }
    };

    fetchCriteres();
    // Supprimer la dépendance vide pour ne s'exécuter qu'une seule fois
  }, []);

  // Calcul du total HT dynamique
  const [totalHT, setTotalHT] = useState(0);

  useEffect(() => {
    const total = (commande.articlesPrestataire || [])
      .reduce((sum, art) => sum + (art?.prixHT || 0), 0);
    setTotalHT(total);
  }, [commande.articlesPrestataire]);

  // Handler pour les champs critères
  const handleCriteresChange = (critere: 'organisation' | 'moyens' | 'social', value: string) => {
    setCommande(prev => ({
      ...prev!,
      criteres: {
        ...prev!.criteres,
        [critere]: {
          ...prev!.criteres?.[critere],
          reponse: value
        }
      }
    }));
    setCriteresErrors(prev => ({ ...prev, [critere]: '' }));
  };

  // Validation des critères
  const validateCriteresForm = () => {
    const newErrors: { [key: string]: string } = {};
    (['organisation', 'moyens', 'social'] as const).forEach((critere) => {
      if (!commande.criteres?.[critere]?.reponse?.trim()) {
        newErrors[critere] = 'Ce champ est requis';
      }
    });
    setCriteresErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateAllFields = () => {
    let valid = true;

    // Vérifier la réponse technique
    if (!commande.reponseTechnique?.trim()) {
      setReponseTechniqueError('Ce champ est requis');
      valid = false;
    } else {
      setReponseTechniqueError('');
    }

    // Vérifier les critères existants
    const newErrors: { [key: string]: string } = {};
    (['organisation', 'moyens', 'social'] as const).forEach((critere) => {
      if (!commande.criteres?.[critere]?.reponse?.trim()) {
        newErrors[critere] = 'Ce champ est requis';
        valid = false;
      }
    });
    setCriteresErrors(newErrors);

    return valid;
  };

  // Sauvegarde en brouillon (API)
  const handleCriteresSaveDraft = async () => {
    setCriteresSaving(true);
    setCriteresSuccessMsg('');
    try {
      const commandeId = window.location.pathname.split('/').pop() || '';
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/subsequents/${commandeId}/criteres`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          criteres: commande.criteres
        })
      });
      if (!response.ok) throw new Error('Erreur lors de la sauvegarde');
      setCriteresSuccessMsg('Vos critères ont bien été sauvegardés.');
    } catch (e) {
      setCriteresSuccessMsg("Erreur lors de la sauvegarde des critères.");
    } finally {
      setCriteresSaving(false);
    }
  };

  // Vérifie que tous les prix HT sont renseignés et supérieurs à 0
  const validatePrixHT = () => {
    const articles = commande.articlesPrestataire || [];

    // Créer un objet d’erreurs (clé = index)
    const newErrors: { [key: number]: boolean } = {};
    articles.forEach((art, index) => {
      if (art.prixHT === undefined || art.prixHT === null || art.prixHT <= 0) {
        newErrors[index] = true;
      }
    });
    if (Object.keys(newErrors).length === 0) {
      setPrixValidated(false); // cache le message quand tout est corrigé
    }
    setPrixErrors(newErrors); // sauvegarde les erreurs pour affichage
    return Object.keys(newErrors).length === 0;
  };

  // Fonction pour vérifier les champs et ouvrir le modal de confirmation
  const handleConfirmSubmit = () => {
    setPrixValidated(true);
    const isPrixValide = validatePrixHT();
    const isAllFieldsValid = validateAllFields();

    if (!isPrixValide) {
      const prixSection = document.querySelector(".articles-table");
      prixSection?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    if (isAllFieldsValid) {
      setIsConfirmModalOpen(true);
    } else {
      const errorElement = document.querySelector(".error, .reponse-technique-error");
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  const handleSaveCandidature = async () => {
    try {
      const commandeId = window.location.pathname.split('/').pop() || '';
      const token = localStorage.getItem('token');
      const formattedArticles = (commande.articlesPrestataire || []).map((ap, index) => ({
        id: Number(commande.articles?.[index].id),
        prixHT: ap.prixHT
      }));

      const submissionData = {
        reponseTechnique: commande.reponseTechnique,
        criteres: commande.criteres,
        articles: formattedArticles,
        departementId: departementInfo?.id,
        regionId: departementInfo?.region?.id,
        // DÉSACTIVÉ TEMPORAIREMENT - Signataires
        // id_signataire: commande.id_signataire,
        // type_signataire: commande.type_signataire
      };

      console.log("[Candidature] Données de sauvegarde:", submissionData);
      // console.log("[Candidature] Informations du signataire envoyées:", {
      //   id_signataire: submissionData.id_signataire,
      //   type_signataire: submissionData.type_signataire
      // });

      const saveCandidature = await fetch(`${process.env.REACT_APP_API_URL}/subsequents/commande/${commandeId}/save`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submissionData)
      });
      if (!saveCandidature.ok) {
        alert('Une erreur est survenue lors de la sauvegarde de votre candidature');
        throw new Error('Erreur lors de la sauvegarde de la candidature');
      } else {
        alert('Candidature sauvegardée avec succès');
        navigate('/commandes');
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde de la candidature:', error);
      alert('Une erreur est survenue lors de la sauvegarde de votre candidature');
    }
  }

  const getDisplayStatus = () => {
    const opStatus = commande?.status;
    const cmdStatus = commande?.operation?.status;

    if (!opStatus && !cmdStatus) return 'Statut inconnu';

    const statusName = opStatus?.nom || cmdStatus?.nom;
    const statusId = opStatus?.id || cmdStatus?.id;

    if (isPrestataireView) {
      const rang = getRangPrestataire();
      if (statusName === `En attente de l'acceptation du rang ${rang}`) {
        return 'À accepter';
      }
    }

    return statusName || 'Statut inconnu';
  };

  // CORRECTION: Déterminer le rang du prestataire depuis les données API
  const getRangPrestataire = () => {
    if (!commande || !isPrestataireView) return 1;
    return commande.rang || 1;
  };

  // CORRECTION: Fonction pour formater le sous-titre
  const getSousTitre = () => {
    const numero = commande?.operation?.numero || commande?.numero;
    const rang = getRangPrestataire();
    return `Suivi du Marché Subséquent n° ${numero}`;
  };

  // CORRECTION: Fonction pour formater le subtitle (lot technique + géographique)
  const getSubtitle = () => {
    const lotTechnique = commande?.operation?.lot_technique || commande?.lot_technique;
    const departement = commande?.operation?.lot_departement?.departement || commande?.lot_departement?.departement;

    if (lotTechnique && departement) {
      return `${lotTechnique.id} - ${lotTechnique.nom}, ${departement.id} - ${departement.nom}`;
    }
    return 'Informations de lot non disponibles';
  };

  // Fonction pour soumettre la candidature
  const handleSubmitCandidature = async () => {
    try {
      const commandeId = window.location.pathname.split('/').pop() || '';
      const token = localStorage.getItem('token');

      if (!token) throw new Error('Token non trouvé, veuillez vous reconnecter.');

      const submissionData = {
        reponseTechnique: commande.reponseTechnique,
        criteres: commande.criteres,
        articles: commande.articlesPrestataire,
        departementId: departementInfo?.id,
        regionId: departementInfo?.region?.id,
        // DÉSACTIVÉ TEMPORAIREMENT - Signataires
        // id_signataire: commande.id_signataire,
        // type_signataire: commande.type_signataire
      };

      console.log("[Candidature] Données soumises pour candidature:", submissionData);
      // console.log("[Candidature] Informations du signataire envoyées:", {
      //   id_signataire: submissionData.id_signataire,
      //   type_signataire: submissionData.type_signataire
      // });

      const response = await fetch(`${process.env.REACT_APP_API_URL}/subsequents/${commandeId}/candidater`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submissionData)
      });

      if (!response.ok) {
        let errorMessage = 'Erreur lors de la soumission de la candidature';
        try {
          const data = await response.json();
          if (data?.message) errorMessage = data.message;
        } catch {
          const text = await response.text();
          if (text) errorMessage = text;
        }
        throw new Error(`API Error (${response.status}): ${errorMessage}`);
      }

      // alert('Candidature soumise avec succès');
      navigate('/commandes');
    } catch (error) {
      if (error instanceof TypeError) {
        alert(`Erreur réseau : ${error.message}`);
      } else {
        alert(`Erreur : ${error.message}`);
      }
    }
  };

  // Fonction pour refuser la consultation
  const handleRefuserConsultation = async () => {
    try {
      const commandeId = window.location.pathname.split('/').pop() || '';
      const token = localStorage.getItem('token');

      if (!token) throw new Error('Token non trouvé, veuillez vous reconnecter.');

      console.log("[Candidature] Refus de la consultation:", commandeId);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/subsequents/${commandeId}/refuser`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        let errorMessage = 'Erreur lors du refus de la consultation';
        try {
          const data = await response.json();
          if (data?.message) errorMessage = data.message;
        } catch {
          const text = await response.text();
          if (text) errorMessage = text;
        }
        throw new Error(`API Error (${response.status}): ${errorMessage}`);
      }

      // Afficher le modal de succès au lieu d'une alert
      setIsRefusSuccessModalOpen(true);
    } catch (error) {
      if (error instanceof TypeError) {
        alert(`Erreur réseau : ${error.message}`);
      } else {
        alert(`Erreur : ${error.message}`);
      }
    }
  };

  // États pour la gestion des signataires
  // DÉSACTIVÉ TEMPORAIREMENT
  /*
  const [signataires, setSignataires] = useState<any[]>([]);
  const [signatairesLoading, setSignatairesLoading] = useState<boolean>(false);
  const [signatairesError, setSignatairesError] = useState<string | null>(null);

  // Ajouter cet état pour maintenir le signataire actuellement sélectionné
  const [selectedSignataireUser, setSelectedSignataireUser] = useState<number | null>(null);
  */

  // Charger les signataires une seule fois quand l'entité est disponible
  // DÉSACTIVÉ TEMPORAIREMENT
  /*
  useEffect(() => {
    if (!commande?.id_entite) return;

    const fetchSignataires = async () => {
      setSignatairesLoading(true);
      setSignatairesError(null);

      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_API_URL}/entites/${commande.id_entite}/signataires`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`Erreur ${response.status} lors de la récupération des signataires`);
        }

        const data = await response.json();
        if (data.status === "success" && Array.isArray(data.data)) {
          console.log("[Candidature] Signataires reçus:", data.data);
          setSignataires(data.data);

          // Si un signataire est déjà sélectionné dans la commande, retrouver son id_user
          if (commande.id_signataire && commande.type_signataire) {
            const matchingUser = data.data.find(s =>
              s.id === commande.id_signataire && s.type_signataire === commande.type_signataire
            );

            if (matchingUser) {
              console.log("[Candidature] Signataire correspondant trouvé:", matchingUser);
              setSelectedSignataireUser(matchingUser.id_user);
              // Mettre à jour la commande pour s'assurer que les infos sont cohérentes
              setCommande(prev => ({
                ...prev,
                id_signataire: matchingUser.id,
                type_signataire: matchingUser.type_signataire,
                prestataire_signataire: {
                  id: matchingUser.id,
                  nom: matchingUser.nom,
                  prenom: matchingUser.prenom,
                  fonction: matchingUser.type_signataire,
                  email: matchingUser.email || '-'
                }
              }));
            } else if (data.data.length > 0) {
              // Si pas de correspondance mais des signataires disponibles, prendre le premier
              console.log("[Candidature] Pas de signataire correspondant, sélection du premier");
              setSelectedSignataireUser(data.data[0].id_user);
              setCommande(prev => ({
                ...prev,
                id_signataire: data.data[0].id,
                type_signataire: data.data[0].type_signataire,
                prestataire_signataire: {
                  id: data.data[0].id,
                  nom: data.data[0].nom,
                  prenom: data.data[0].prenom,
                  fonction: data.data[0].type_signataire,
                  email: data.data[0].email || '-'
                }
              }));
            }
          } else if (data.data.length > 0) {
            // Aucun signataire sélectionné, prendre le premier
            console.log("[Candidature] Aucun signataire sélectionné, prendre le premier");
            setSelectedSignataireUser(data.data[0].id_user);
            setCommande(prev => ({
              ...prev,
              id_signataire: data.data[0].id,
              type_signataire: data.data[0].type_signataire,
              prestataire_signataire: {
                id: data.data[0].id,
                nom: data.data[0].nom,
                prenom: data.data[0].prenom,
                fonction: data.data[0].type_signataire,
                email: data.data[0].email || '-'
              }

            }));
          }
        }
      } catch (error) {
        console.error("Erreur lors du chargement des signataires:", error);
        setSignatairesError("Impossible de charger la liste des signataires");
      } finally {
        setSignatairesLoading(false);
      }
    };

    fetchSignataires();
  }, [commande?.id_entite]);
  */

  // Handler pour le changement de signataire
  // DÉSACTIVÉ TEMPORAIREMENT
  /*
  const handleSignataireChange = (id: number, type_signataire: string) => {
    console.log("[Candidature] Nouveau signataire sélectionné:", { id, type_signataire });

    // Important: mettre à jour selectedSignataireUser en cherchant l'id_user correspondant
    const selectedSig = signataires.find(s => s.id === id && s.type_signataire === type_signataire);

    if (selectedSig) {
      console.log("[Candidature] Mise à jour du signataire avec:", selectedSig);
      setSelectedSignataireUser(selectedSig.id_user);

      setCommande(prev => {
        // Créer un nouvel objet pour forcer le re-rendu
        const updatedCommande = {
          ...prev,
          id_signataire: id,
          type_signataire: type_signataire,
          prestataire_signataire: {  // Ajout de cet objet
            id: selectedSig.id,
            nom: selectedSig.nom,
            prenom: selectedSig.prenom,
            email: selectedSig.email || '-'
          }
        };

        // Force log pour vérification
        console.log("[Candidature] Commande mise à jour:", updatedCommande);
        return updatedCommande;
      });
    }
  };
  */

  console.log("commande.formulaire:", commande);

  return (
    <div className="tab-candidature">
      {/* Affichage des informations du département si disponibles */}
      {departementInfo && (
        <section>
          <h3 className='section-header'>Informations du département</h3>
          <div className="section-content">
            <div className="form-group">
              <label>Département</label>
              <div className="input-inactif">{departementInfo.nom || '-'}</div>
            </div>
            <div className="form-group">
              <label>Région</label>
              <div className="input-inactif">{departementInfo.region?.nom || '-'}</div>
            </div>
          </div>
        </section>
      )}

      {/* Première partie : Descriptif Administratif */}
      <section>
        {isOperationExpired && (
          <div className="expired-notice" style={{
            backgroundColor: '#fee',
            border: '2px solid #c33',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '24px',
            color: '#c33',
            fontWeight: 'bold',
            textAlign: 'center'
          }}>
            Cette commande est expirée. Vous ne pouvez plus soumettre de candidature ni modifier vos réponses.
          </div>
        )}
        <h3 className='section-header'>Descriptif Administratif</h3>
        <div className="section-content">
          <FormulaireAdministratif
            formData={commande.formulaire || {}}
            commande={commande}
            operation={commande.operation}
            viewMode={true}
            hideBesoin={true}
          />
        </div>
      </section>
      <div className="page-break" />


      {/* Deuxième partie : Descriptif Technique */}
      <section>
        <h3 className='section-header'>Descriptif Technique</h3>
        <div className="section-content">
          <div className="form-group">
            <label>Définition du besoin</label>
            <div className="input-inactif definition-besoin">
              {commande.operation?.besoin || commande.formulaire?.besoin || "Pas précisé"}
            </div>
          </div>
          <div className="form-group">
            <label>Réponse Technique au Besoin <span style={{ color: 'red' }}>*</span></label>
            <textarea
              value={commande.reponseTechnique || ''}
              onChange={(e) => {
                setCommande(prev => ({
                  ...prev!,
                  reponseTechnique: e.target.value
                }));
                setReponseTechniqueError(''); // supprimer l'erreur à la saisie
              }}
              className={`definition-besoin-textarea ${reponseTechniqueError ? 'error' : ''}`}
              placeholder={isOperationExpired ? "Opération expirée - Réponse non modifiable" : "Décrivez votre réponse technique au besoin exprimé"}
              rows={15}
              disabled={(commande.status && commande.status.id >= 4) || isOperationExpired}
            />
            {reponseTechniqueError && <span className="error-message reponse-technique-error">{reponseTechniqueError}</span>}
          </div>

          <div className="form-group critere-section">
            <label>
              Critère Organisation <span className="required">*</span>
            </label>
            <textarea
              value={commande.criteres?.organisation?.reponse || commande.criteres?.organisation?.reponse === '' ? commande.criteres?.organisation?.reponse : ''}
              onChange={e => handleCriteresChange('organisation', e.target.value)}
              placeholder={isOperationExpired ? "Opération expirée - Réponse non modifiable" : "Décrivez votre organisation pour ce marché"}
              className={criteresErrors.organisation ? 'error' : ''}
              disabled={(commande.status && commande.status.id >= 4) || isOperationExpired}
            />
            {criteresErrors.organisation && <span className="error-message">{criteresErrors.organisation}</span>}
          </div>
          <div className="form-group critere-section">
            <label>
              Critère Moyens mis en oeuvre<span className="required">*</span>
            </label>
            <textarea
              value={commande.criteres?.moyens?.reponse || ''}
              onChange={e => handleCriteresChange('moyens', e.target.value)}
              placeholder={isOperationExpired ? "Opération expirée - Réponse non modifiable" : "Décrivez les moyens que vous mobilisez"}
              className={criteresErrors.moyens ? 'error' : ''}
              disabled={(commande.status && commande.status.id >= 4) || isOperationExpired}

            />
            {criteresErrors.moyens && <span className="error-message">{criteresErrors.moyens}</span>}
          </div>
          <div className="form-group critere-section">
            <label>
              Critère Social & Environnemental <span className="required">*</span>
            </label>
            <textarea
              value={commande.criteres?.social?.reponse || ''}
              onChange={e => handleCriteresChange('social', e.target.value)}
              placeholder={isOperationExpired ? "Opération expirée - Réponse non modifiable" : "Décrivez vos engagements sociaux et environnementaux"}
              className={criteresErrors.social ? 'error' : ''}
              disabled={(commande.status && commande.status.id >= 4) || isOperationExpired}
            />
            {criteresErrors.social && <span className="error-message">{criteresErrors.social}</span>}
          </div>
        </div>
      </section>
      <div className="page-break" />


      {/* Troisième partie : Descriptif Financier avec prix HT editable */}
      <section>
        <h3 className='section-header'>Offre Financière</h3>
        <div className="section-content">
          <table className="articles-table">
            <thead>
              <tr>
                <th className='financier-numero-article'>N°</th>
                <th>Description de la prestation</th>
                <th className="financier-prix">Prix forfaitaire HT</th>
              </tr>
            </thead>
            <tbody>
              {(commande.articles || []).map((article, index) => (
                <tr key={article.numero || index} className={`${prixErrors[index] ? 'prix-error-cell' : ''}`}>
                  <td className='financier-numero-article'>{article.code}</td>
                  <td>{article.nom}</td>
                  <td className={`financier-prix`}>
                    <div className="prix-input-container">
                      <NumericFormat
                        value={commande.articlesPrestataire?.[index]?.prixHT ?? ''}
                        disabled={(commande.status && commande.status.id >= 4) || isOperationExpired}
                        thousandSeparator=" "
                        decimalSeparator=","
                        decimalScale={2}
                        fixedDecimalScale
                        allowNegative={false}
                        allowLeadingZeros={false}
                        isNumericString
                        placeholder='0,00'
                        className={`prix-input`}
                        isAllowed={(values) => {
                          const { floatValue } = values;
                          return floatValue === undefined || floatValue <= 1000000000;
                        }}
                        onFocus={(e) => {
                          const valueStr = e.target.value;
                          const commaIndex = valueStr.indexOf(',');
                          if (commaIndex !== -1) {
                            e.target.setSelectionRange(commaIndex + 1, valueStr.length);
                          }
                        }}
                        onValueChange={(vals: NumberFormatValues) => {
                          const newArts = [...(commande.articlesPrestataire || [])];
                          if (!newArts[index]) {
                            newArts[index] = { id: article.numero, prixHT: 0 };
                          }
                          newArts[index].prixHT = vals.floatValue ?? 0;
                          setCommande(prev => ({ ...prev!, articlesPrestataire: newArts }));

                          // ✅ Si l'utilisateur tape un prix > 0, on supprime l'erreur correspondante
                          setPrixErrors(prev => {
                            const updated = { ...prev };
                            if (vals.floatValue && vals.floatValue > 0) {
                              delete updated[index];
                            }
                            return updated;
                          });
                        }}
                      />
                      <span className="prix-currency">€</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {prixValidated && Object.keys(prixErrors).length > 0 && (
            <div className="error-global-prix">
              Veuillez renseigner un prix HT valide pour tous les articles avant de soumettre votre candidature.
            </div>
          )}
          <div className="total-section">
            <div className="total-row">
              <span>Total DPGF HT </span>
              <span>
                {totalHT.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
              </span>
            </div>
          </div>

          <div className='honoraires-container'>
            <p>
              Pour mémoire, conformément aux pièces du marchés et aux conditions générales acceptées, Centralis vous facturera 5% de commissions sur le montant de votre offre HT.
              Il vous appartient d'anticiper et de prendre en compte ce principe dans votre offre financière. &nbsp;
            </p>
            <span className='honoraires'>
              En l'état du remplissage de votre offre, les honoraires sont évalués à :&nbsp;
              {(totalHT * 0.05).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
              &nbsp;HT.
            </span>
          </div>
        </div>
      </section>
      <div className="page-break" />


      {/* Section pour les informations de signature */}
      {/* DÉSACTIVÉ TEMPORAIREMENT */}
      {/*
      <section>
        <h3 className='section-header'>Informations de signature</h3>
        <div className="section-content">
          <div className="form-group">
            <label>Signataire <span style={{ color: 'red' }}>*</span></label>
            <DropDownSignataires
              signataires={signataires}
              value={selectedSignataireUser} // Utiliser l'état local selectedSignataireUser
              onChange={handleSignataireChange}
              loading={false}
              error={null}
              disabled={signatairesLoading || (commande.status && commande.status.id >= 4) || isOperationExpired}
            />
            {(!commande.id_signataire && commande.status && commande.status.id < 4) &&
              <span className="error-message">Veuillez sélectionner un signataire</span>
            }
          </div>
          <p>
            Nous attirons votre attention sur
            l'importance de l'exactitude du nom, et prénom du signataire qui sera vérifié par notre partenire Yousign
            pour la signature éléctronique.
          </p>
        </div>
      </section>
      */}

      {commande.status && commande.status.id < 4 && !isOperationExpired ? (
        <div className="action-buttons no-print">
          {/* Bouton "Refuser la consultation" - uniquement en statut 3, placé à gauche */}
          {commande.status && commande.status.id === 3 && (
            <Button
              value="Refuser la consultation"
              variant="secondary"
              onClick={() => setIsRefuserModalOpen(true)}
            />
          )}
          
          <Button
            value="Enregistrer et quitter"
            variant="secondary"
            onClick={handleSaveCandidature}
          />
          {/* <Button
            value="PDF"
            icon={<PdfIcon />}
            variant="secondary"
            onClick={handleExport}
          /> */}
          <PrintButton
            targetRef={printRef}
            fileName={`${commande?.numero || commande?.operation?.numero} - Analyse`}
            suiviHeaderProps={{
              titre: commande?.operation?.nom || commande?.nom,
              sousTitre: getSousTitre(), // fonction disponible dans le parent
              subtitle: getSubtitle(),   // fonction disponible dans le parent
              status: getDisplayStatus(),
              statusColor: getStatusColor(getDisplayStatus()),
              delais: commande?.expire_at || commande?.operation?.expire_at,
              backgroundImage: getLotImage(commande?.operation?.lot_technique?.nom || commande?.lot_technique?.nom),
              statusId: commande?.status?.id,
              source: commande?.source || commande?.operation?.source,
            }}
          />

          <Button
            value="Soumettre la candidature"
            variant="primary"
            onClick={handleConfirmSubmit}
          />
        </div>
      ) : (
        <div className="action-buttons no-print">
          <PrintButton
            targetRef={printRef}
            fileName={`${commande?.numero || commande?.operation?.numero} - Analyse`}
            suiviHeaderProps={{
              titre: commande?.operation?.nom || commande?.nom,
              sousTitre: getSousTitre(), // fonction disponible dans le parent
              subtitle: getSubtitle(),   // fonction disponible dans le parent
              status: getDisplayStatus(),
              statusColor: getStatusColor(getDisplayStatus()),
              delais: commande?.expire_at || commande?.operation?.expire_at,
              backgroundImage: getLotImage(commande?.operation?.lot_technique?.nom || commande?.lot_technique?.nom),
              statusId: commande?.status?.id,
              source: commande?.source || commande?.operation?.source,
            }}
          />
        </div>
      )}

      {/* Modal de confirmation pour soumettre la candidature */}
      <ValidationPopUp
        isOpen={isConfirmModalOpen}
        title={"Dépôt de la candidature"}
        text="Vous vous apprêtez à déposer votre candidature. Il n'est plus possible de modifier votre offre après cette étape. Voulez-vous continuer ?"
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={() => {
          setIsConfirmModalOpen(false);
          handleSubmitCandidature();
        }}
        cancelLabel="Non"
        confirmLabel="Oui"
      />

      {/* Modal de confirmation pour refuser la consultation */}
      <ValidationPopUp
        isOpen={isRefuserModalOpen}
        title={"Refuser la consultation"}
        text="Vous vous apprêtez à refuser de candidater à ce marché subséquent. Cette action est irréversible."
        onClose={() => setIsRefuserModalOpen(false)}
        onConfirm={() => {
          setIsRefuserModalOpen(false);
          handleRefuserConsultation();
        }}
        cancelLabel="Annuler"
        confirmLabel="Refuser"
      />

      {/* Modal de succès après refus de consultation */}
      <ValidationPopUp
        isOpen={isRefusSuccessModalOpen}
        title={"Consultation refusée"}
        text="La consultation a été refusée avec succès. Vous allez être redirigé vers la liste des commandes."
        onClose={() => {
          setIsRefusSuccessModalOpen(false);
          navigate('/commandes');
        }}
        onConfirm={() => {
          setIsRefusSuccessModalOpen(false);
          navigate('/commandes');
        }}
        cancelLabel=""
        confirmLabel="OK"
      />
    </div>
  );
};

export default Candidature;